import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { refreshProfile } from "@/src/auth/store/auth.thunks";
import { Button } from "@/src/common/components/ui/button";
import { InputGroup } from "@/src/common/components/ui/input";
import { useAppDispatch } from "@/src/common/redux/store";
import { supabase } from "@/src/common/services/supabase.service";
import { toastService } from "@/src/common/services/toast.service";
import { AvatarPicker } from "@/src/profile/components/avatar-picker";
import { AvatarUploadError, avatarUploadService } from "@/src/profile/services/avatar-upload.service";

const accountSchema = z.object({
	first_name: z.string().trim().min(1, "First name is required").max(120),
	last_name: z.string().trim().min(1, "Last name is required").max(120),
});

type AccountValues = z.infer<typeof accountSchema>;

export function AccountSection() {
	const { t } = useTranslation("common");
	const { profile } = useAuth();
	const dispatch = useAppDispatch();
	const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

	const defaults: AccountValues = {
		first_name: profile?.first_name ?? "",
		last_name: profile?.last_name ?? "",
	};

	const handleAvatarPick = async (file: File) => {
		if (!profile?.id) return;
		setIsUploadingAvatar(true);
		try {
			await avatarUploadService.upload(profile.id, file);
			await dispatch(refreshProfile());
			toastService.success(t("settings.account.avatar_success"));
		} catch (err) {
			console.error("[settings] avatar upload failed", err);
			toastService.error(err instanceof AvatarUploadError ? t("settings.account.avatar_unsupported") : t("settings.account.avatar_error"));
		} finally {
			setIsUploadingAvatar(false);
		}
	};

	const { control, handleSubmit, formState, reset } = useForm<AccountValues>({
		resolver: zodResolver(accountSchema),
		mode: "onChange",
		defaultValues: defaults,
	});

	useEffect(() => {
		reset({ first_name: profile?.first_name ?? "", last_name: profile?.last_name ?? "" });
	}, [profile?.first_name, profile?.last_name, reset]);

	const onSubmit = handleSubmit(async (values) => {
		if (!profile?.id) return;
		try {
			const { error } = await supabase
				.from("profiles")
				.update({ first_name: values.first_name.trim(), last_name: values.last_name.trim() })
				.eq("id", profile.id);
			if (error) throw new Error(error.message);
			await dispatch(refreshProfile());
			reset(values);
			toastService.success(t("settings.account.success"));
		} catch {
			toastService.error(t("settings.account.error"));
		}
	});

	const fullName = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim() || profile?.email;

	return (
		<form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
			<div className="flex items-center gap-4">
				<AvatarPicker
					src={profile?.avatar_url}
					name={fullName}
					size="xl"
					onPick={handleAvatarPick}
					isLoading={isUploadingAvatar}
					ariaLabel={t("aria.change_avatar")}
				/>
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-medium text-foreground">{t("settings.account.avatar_label")}</span>
					<span className="text-xs text-muted-foreground">{t("settings.account.avatar_help")}</span>
				</div>
			</div>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<InputGroup control={control} name="first_name" label={t("settings.account.first_name")} isRequired />
				<InputGroup control={control} name="last_name" label={t("settings.account.last_name")} isRequired />
			</div>
			{profile?.email ? (
				<div className="flex flex-col gap-1">
					<span className="text-xs font-medium text-foreground">{t("settings.account.email")}</span>
					<span className="text-sm text-muted-foreground">{profile.email}</span>
					<span className="text-[11px] text-muted-foreground">{t("settings.account.email_help")}</span>
				</div>
			) : null}
			<div className="flex justify-end">
				<Button
					type="submit"
					variant="primary"
					isDisabled={!formState.isDirty || !formState.isValid || formState.isSubmitting}
					isPending={formState.isSubmitting}
				>
					{t("settings.account.save")}
				</Button>
			</div>
		</form>
	);
}
